/**
 * O projeto em si — o que ele é, o que ele vende e quem trabalha nele.
 *
 * Três coisas que no menu do Sistema de hoje são itens separados (Editar,
 * Editar Observação, Produto(s), Usuários) e que aqui ficam juntas porque são a
 * mesma pergunta: *o que é este projeto?*
 *
 * **Solicitar atualização tem lugar próprio.** É a RN-05 na mão: marca o
 * projeto para o gráfico ser refeito. Toda escrita já faz isso sozinha, então o
 * botão existe para o caso em que alguém mexeu por fora — e a tela diz isso,
 * senão parece um botão que não faz nada.
 */

import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, noProjeto, obter } from "@/api/cliente"
import { chaves } from "@/api/query"
import { usarProdutos } from "@/api/modelos"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import type { ProjetoResponse } from "@/api/tipos"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { CATEGORIAS } from "./categorias"
import { usarProdutosDoProjeto, usarSalvarProduto } from "@/api/produtosDoProjeto"

type ProjetoPainel = { projetoId: number; painelId: number; status: number; nome: string }


export function DadosDoProjeto() {
  const { projetoId } = useParams()
  const id = Number(projetoId)
  const qc = useQueryClient()

  const projeto = useQuery({
    queryKey: chaves.projeto(id),
    enabled: id > 0,
    queryFn: () => obter<ProjetoResponse>(`api/projetos/${id}`, {}),
  })

  const produtos = usarProdutosDoProjeto(id)

  const equipe = useQuery({
    queryKey: ["projeto-paineis", id] as const,
    enabled: id > 0,
    queryFn: () => obter<ProjetoPainel[]>(`api/projetos/${id}/paineis`, {}),
  })

  const catalogoProdutos = usarProdutos()

  const [descricao, setDescricao] = useState("")
  const [categoria, setCategoria] = useState("")
  const [concluido, setConcluido] = useState(0)
  const [obs, setObs] = useState("")

  useEffect(() => {
    const p = projeto.data
    if (!p) return
    setDescricao(p.descricao ?? "")
    setCategoria(p.categoria ?? "")
    setConcluido(p.concluido ?? 0)
    setObs(p.obs ?? "")
  }, [projeto.data])

  const salvar = useMutation({
    mutationFn: async () => {
      // Dois endpoints porque o legado separa: os dados do projeto num, a
      // observação noutro. Mandar os dois de uma vez inventaria um contrato.
      await api.put(`api/projetos/${id}`, { descricao, categoria, concluido }, noProjeto(id))
      await api.patch(`api/projetos/${id}/obs`, { obs }, noProjeto(id))
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chaves.projeto(id) })
      qc.invalidateQueries({ queryKey: ["projetos"] })
    },
  })

  const pedirAtualizacao = useMutation({
    mutationFn: async () => {
      await api.post(`api/projetos/${id}/solicitar-atualizacao`, undefined, noProjeto(id))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: chaves.projeto(id) }),
  })

  /**
   * Consolidar aterramento: marca `tipo = 1` em todo circuito cujo prefixo (até
   * o primeiro ponto) é usado por um componente de aterramento do projeto.
   *
   * **Reescreve dados em massa** e não tem desfazer — por isso a confirmação é
   * de duas etapas e diz o que vai acontecer, não "tem certeza?".
   */
  const consolidar = useMutation({
    mutationFn: async () =>
      // A resposta traz os PREFIXOS que serviram de base, e não só a contagem —
      // é o que permite conferir se o resultado bate com o esperado.
      (await api.post<{ projetoId: number; prefixos: string[]; circuitosMarcados: number }>(
        "api/pinouts/consolidar-aterramento", undefined,
        noProjeto(id, { params: { projetoId: id } }))).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pinouts", id] })
      qc.invalidateQueries({ queryKey: chaves.projeto(id) })
    },
  })

  const removerDaEquipe = useMutation({
    mutationFn: async (painelId: number) => {
      await api.delete(`api/projetos/${id}/paineis/${painelId}`, noProjeto(id))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["projeto-paineis", id] }),
  })

  const [confirmandoAterramento, setConfirmandoAterramento] = useState(false)

  // Manda a data de publicação junto — sem ela o PUT gravava 01/01/1900 a cada clique.
  const salvarProduto = usarSalvarProduto(id)

  const excluirProduto = useMutation({
    mutationFn: async (produtoId: number) => {
      await api.delete(`api/projetos/${id}/produtos/${produtoId}`, noProjeto(id))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["projeto-produtos", id] }),
  })

  const p = projeto.data
  const mudou = !!p && (
    descricao !== (p.descricao ?? "") || categoria !== (p.categoria ?? "")
    || concluido !== (p.concluido ?? 0) || obs !== (p.obs ?? ""))

  const erro = salvar.error ?? pedirAtualizacao.error ?? salvarProduto.error
    ?? excluirProduto.error ?? consolidar.error ?? removerDaEquipe.error

  const [novoProduto, setNovoProduto] = useState(-1)

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 18 }}>
        <div style={{ maxWidth: 700, display: "flex", flexDirection: "column", gap: 22 }}>

          <section style={{ display: "flex", flexDirection: "column", gap: 11 }}>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>O projeto</h2>

            <Rotulo texto="Descrição">
              <input className="campo" value={descricao} style={entrada}
                     onChange={(e) => setDescricao(e.target.value)} />
            </Rotulo>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Rotulo texto="Categoria">
                <select className="campo" value={categoria} style={entrada}
                        onChange={(e) => setCategoria(e.target.value)}>
                  {!(CATEGORIAS as readonly string[]).includes(categoria) && categoria !== "" && (
                    // Categoria gravada que não está na lista: mostra em vez de
                    // trocar calado pela primeira opção.
                    <option value={categoria}>{categoria}</option>
                  )}
                  {CATEGORIAS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Rotulo>

              <Rotulo texto="Situação">
                <select className="campo" value={concluido} style={entrada}
                        onChange={(e) => setConcluido(Number(e.target.value))}>
                  <option value={0}>Em andamento</option>
                  <option value={1}>Concluído</option>
                </select>
              </Rotulo>
            </div>

            <Rotulo texto="Observação">
              <textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={3}
                        style={{ ...entrada, height: "auto", padding: "7px 9px", resize: "vertical", lineHeight: 1.45 }} />
            </Rotulo>

            {mudou && (
              <button
                onClick={() => salvar.mutate()}
                disabled={salvar.isPending}
                style={{
                  alignSelf: "flex-start", height: 32, padding: "0 13px", borderRadius: 7,
                  background: "var(--brand)", color: "#FFFFFF", fontSize: 12.5, fontWeight: 600,
                }}
              >
                {salvar.isPending ? "Salvando…" : "Salvar"}
              </button>
            )}
          </section>

          <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Atualização do gráfico</h2>
            <p style={{ margin: 0, fontSize: 12, color: "var(--txt2)", lineHeight: 1.5 }}>
              {p?.atualizar
                ? "Este projeto está marcado para reprocessar — o gráfico será refeito."
                : "O gráfico deste projeto está em dia."}{" "}
              Toda escrita marca sozinha (RN-05); o botão serve para o caso de alguém ter mexido por fora.
            </p>
            <button
              onClick={() => pedirAtualizacao.mutate()}
              disabled={pedirAtualizacao.isPending}
              style={{
                alignSelf: "flex-start", height: 30, padding: "0 12px", borderRadius: 7,
                border: "1px solid var(--line2)", background: "var(--surface)",
                fontSize: 12, fontWeight: 600, color: "var(--txt2)",
              }}
            >
              {pedirAtualizacao.isPending ? "Marcando…" : "Solicitar atualização"}
            </button>
          </section>

          <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Consolidar aterramento</h2>
            <p style={{ margin: 0, fontSize: 12, color: "var(--txt2)", lineHeight: 1.5 }}>
              Marca como <b>aterramento</b> todo circuito cujo prefixo — o que vem antes do primeiro
              ponto — já é usado por um componente de aterramento deste projeto. É o que faz o
              desenho colapsar esses circuitos no símbolo de terra.
            </p>

            {confirmandoAterramento ? (
              <div style={{
                padding: "11px 12px", borderRadius: 9, display: "flex", flexDirection: "column", gap: 8,
                background: "var(--acao-soft)", color: "var(--acao-on-soft)", border: "1px solid var(--acao)",
              }}>
                <span style={{ fontSize: 11.5, lineHeight: 1.55 }}>
                  Isto <b>reescreve o tipo de muitos pinouts de uma vez</b> e não tem desfazer. Vale
                  conferir antes que os componentes de aterramento do projeto estejam certos — é deles
                  que sai a lista de prefixos.
                </span>
                <span style={{ display: "flex", gap: 7 }}>
                  <button
                    onClick={() => { consolidar.mutate(); setConfirmandoAterramento(false) }}
                    disabled={consolidar.isPending}
                    style={{
                      height: 30, padding: "0 12px", borderRadius: 7, fontSize: 12, fontWeight: 600,
                      background: "var(--acao)", color: "var(--acao-ink)",
                    }}
                  >
                    {consolidar.isPending ? "Consolidando\u2026" : "Confirmar e consolidar"}
                  </button>
                  <button onClick={() => setConfirmandoAterramento(false)}
                          style={{ height: 30, padding: "0 10px", fontSize: 12, color: "var(--txt2)" }}>
                    Cancelar
                  </button>
                </span>
              </div>
            ) : (
              <button
                onClick={() => setConfirmandoAterramento(true)}
                style={{
                  alignSelf: "flex-start", height: 30, padding: "0 12px", borderRadius: 7,
                  border: "1px solid var(--line2)", background: "var(--surface)",
                  fontSize: 12, fontWeight: 600, color: "var(--txt2)",
                }}
              >
                Consolidar aterramento
              </button>
            )}

            {consolidar.data != null && (
              <div style={{ fontSize: 11.5, color: "var(--ok-on-soft)", lineHeight: 1.55 }}>
                <b>{consolidar.data.circuitosMarcados}</b>{" "}
                {consolidar.data.circuitosMarcados === 1 ? "circuito marcado" : "circuitos marcados"}.
                {consolidar.data.prefixos.length > 0 ? (
                  <span style={{ display: "block", color: "var(--txt3)" }}>
                    a partir dos prefixos{" "}
                    <span className="mono">{consolidar.data.prefixos.slice(0, 12).join(", ")}</span>
                    {consolidar.data.prefixos.length > 12
                      && <> e mais {consolidar.data.prefixos.length - 12}</>}
                  </span>
                ) : (
                  <span style={{ display: "block", color: "var(--txt3)" }}>
                    Nenhum prefixo de aterramento neste projeto — nada foi alterado.
                  </span>
                )}
              </div>
            )}
          </section>

          <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Produtos</h2>
            <p style={{ margin: 0, fontSize: 12, color: "var(--txt2)" }}>
              Em que produtos este projeto aparece para o cliente.
            </p>

            {(produtos.data ?? []).length === 0 ? (
              <p style={{ margin: 0, fontSize: 12.5, color: "var(--txt3)" }}>Nenhum produto vinculado.</p>
            ) : (
              <div style={{ border: "1px solid var(--line2)", borderRadius: 10, overflow: "hidden" }}>
                {(produtos.data ?? []).map((x, i) => (
                  <div key={x.produtoId} style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "9px 12px",
                    borderTop: i === 0 ? undefined : "1px solid var(--line)", background: "var(--surface)",
                  }}>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 12.5 }}>{x.produtoNome}</span>
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--txt2)" }}>
                      <input type="checkbox" checked={x.publicado}
                             onChange={(e) => salvarProduto.mutate({
                               produtoId: x.produtoId, publicado: e.target.checked,
                               publicadoPinout: x.publicadoPinout })} />
                      publicado
                    </label>
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--txt2)" }}>
                      <input type="checkbox" checked={x.publicadoPinout}
                             onChange={(e) => salvarProduto.mutate({
                               produtoId: x.produtoId, publicado: x.publicado,
                               publicadoPinout: e.target.checked })} />
                      pinout
                    </label>
                    <button onClick={() => excluirProduto.mutate(x.produtoId)}
                            style={{ fontSize: 11.5, color: "var(--erro)" }}>
                      remover
                    </button>
                  </div>
                ))}
              </div>
            )}

            <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <select className="campo" value={novoProduto} style={{ ...entrada, width: 260 }}
                      onChange={(e) => setNovoProduto(Number(e.target.value))}>
                <option value={-1}>Acrescentar produto…</option>
                {catalogoProdutos.itens
                  .filter((c) => !(produtos.data ?? []).some((x) => x.produtoId === c.id))
                  .map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
              {novoProduto >= 0 && (
                <button
                  onClick={() => {
                    salvarProduto.mutate({ produtoId: novoProduto, publicado: false, publicadoPinout: true })
                    setNovoProduto(-1)
                  }}
                  style={{
                    height: 30, padding: "0 12px", borderRadius: 7, background: "var(--brand)",
                    color: "#FFFFFF", fontSize: 12, fontWeight: 600,
                  }}
                >
                  Acrescentar
                </button>
              )}
            </span>
          </section>

          <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Equipe</h2>
            <p style={{ margin: 0, fontSize: 12, color: "var(--txt2)" }}>
              Quem está vinculado a este projeto. É registro, e vem do cadastro de painéis.
            </p>
            {(equipe.data ?? []).length === 0 ? (
              <p style={{ margin: 0, fontSize: 12.5, color: "var(--txt3)" }}>
                Ninguém vinculado a este projeto.
              </p>
            ) : (
              <div style={{ border: "1px solid var(--line2)", borderRadius: 10, overflow: "hidden" }}>
                {(equipe.data ?? []).map((x, i) => (
                  <div key={x.painelId} style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "9px 12px",
                    borderTop: i === 0 ? undefined : "1px solid var(--line)", background: "var(--surface)",
                  }}>
                    <span style={{ flex: 1, fontSize: 12.5 }}>{x.nome}</span>
                    <span className="num" style={{ fontSize: 11, color: "var(--txt3)" }}>status {x.status}</span>
                    <button
                      onClick={() => removerDaEquipe.mutate(x.painelId)}
                      disabled={removerDaEquipe.isPending}
                      title="Tira esta pessoa do projeto. O cadastro dela continua."
                      style={{ fontSize: 11.5, color: "var(--erro)" }}
                    >
                      remover
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {erro != null && (
            <div style={{
              padding: "10px 12px", borderRadius: 8, fontSize: 11.5, lineHeight: 1.5,
              background: erro instanceof ErroDeTrava ? "var(--acao-soft)" : "var(--erro-soft)",
              color: erro instanceof ErroDeTrava ? "var(--acao-on-soft)" : "var(--erro-on-soft)",
            }}>
              {erro instanceof ErroDaApi || erro instanceof Error ? erro.message : "Não foi possível gravar."}
            </div>
          )}
        </div>
      </div>

      <BarraDeEstado>
        {p && (
          <span>
            projeto <b className="num" style={{ color: "var(--txt)" }}>{p.id}</b>
            {p.atualizar && <span style={{ color: "var(--acao-on-soft)" }}> · marcado para reprocessar</span>}
          </span>
        )}
      </BarraDeEstado>
    </div>
  )
}

const entrada: React.CSSProperties = {
  width: "100%", padding: "0 9px", borderRadius: 7,
  border: "1px solid var(--line2)", background: "var(--surface2)", fontSize: 12.5,
}

function Rotulo({ texto, children }: { texto: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--txt2)" }}>{texto}</span>
      {children}
    </label>
  )
}
